import { Component, DestroyRef, EventEmitter, HostListener, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { defer, fromEvent, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OrderDetailsShot } from '../../../../models/common/order-details-shot.model';
import { OrderStatus } from '../../../../models/common/order-status.models';
import { SidebarTypeEnum } from '../../../../models/common/sidebar-type.enum';
import { PhotographerOrderDetails } from '../../models/photographer-order-details.model';
import { UploadedShot } from '../../models/uploaded-shot.model';
import { PhotographerService } from '../../../../services/photographer.service';
import { BasicFile } from '../../../../shared/models/basic-file.model';
import { ShotStatusEnum } from '../../../../shared/models/shot-status.enum';
import { TopbarService } from '../../../../shared/services/topbar.service';
import { PhotoData } from '../../models/photo-data.type';
import { ExcelService } from '../../../../services/excel.service';

@Component({
  standalone: false,
  selector: 'bt-photographer-order-info-auto-sorting',
  templateUrl: './photographer-order-info-auto-sorting.component.html',
  styleUrls: ['./photographer-order-info-auto-sorting.component.scss']
})
export class PhotographerOrderInfoAutoSortingComponent implements OnChanges, OnDestroy {

  @Input() public orderId: number;
  @Input() public orderNumber: string;
  @Input() public modelId: number;

  @Output() public exit = new EventEmitter<any>();

  readonly #destroyRef = inject(DestroyRef);
  readonly #excelService = inject(ExcelService);

  public sortedImages: { [key: string]: Array<UploadedShot> } = {};

  public orderDetails: { orderStatus?: OrderStatus, data?: Array<PhotographerOrderDetails>, dataToView?: Array<PhotographerOrderDetails>, selected?: PhotographerOrderDetails, search: string, totalLength?: number, areFiltered?: boolean } = {
    search: ''
  };
  public shotStatusEnum = ShotStatusEnum;
  public uploadedShots: UploadedShot[];
  public tmpShot: UploadedShot;
  public basicFile: BasicFile;
  public currentSidebarType: SidebarTypeEnum;
  public sidebarTypeEnum = SidebarTypeEnum;
  public orderDetailsLoaderId = 'orderDetailsLoaderId';
  public uploadedShotsLoaderId = 'uploadedShotsLoaderId';
  public sidebarLoaderId = 'sidebarLoaderId';

  public dropzoneActive = false;


  // drag&drop variables
  public inDragging: boolean;
  public elementInDrag: HTMLElement;
  public elementInDragId = 'elementInDragId';
  public overedData: { shot?: OrderDetailsShot, style?: PhotographerOrderDetails } = {};

  private subscription = new Subscription();

  constructor(
    private photoService: PhotographerService,
    private topbarService: TopbarService
  ) {
    this.searchTextSubscription();
  }

  @HostListener('window:dragover', ['$event'])
  public windowDragover(ev: any) {
    this.overedData = {};
    this.onDragOver(ev);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.orderId) {
      if (this.orderId) {
        this.getPhotographerOrderDetails();
        this.getPhotographerUploadedShots();
      } else {
        this.orderDetails.data = null;
      }
    }
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }


  public onDropZoneDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dropzoneActive = true;
  }

  public onDropZoneDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dropzoneActive = false;
  }

  public onDropZoneDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dropzoneActive = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.onImagesUpload({ target: { files } });
    }
  }

  public onStyleComments(item: PhotographerOrderDetails) {
    item.showMenu = false;
    this.orderDetails.selected = item;
    this.currentSidebarType = SidebarTypeEnum.shotsComments;
  }

  public setStylesToView(fromSearch?: boolean) {
    const search = this.orderDetails.search ? this.orderDetails.search.toLowerCase() : '';
    this.orderDetails.dataToView = this.orderDetails.data.filter(x => x.fullName.toLowerCase().indexOf(search) >= 0);
    this.orderDetails.areFiltered = !!this.orderDetails.search && (this.orderDetails.areFiltered || fromSearch);
  }

  public onShowShotPreview(s: UploadedShot) {
    s.showMenu = false;
    this.basicFile = new BasicFile({ FileName: s.fileName, FilePath: s.imgPath });
  }

  public onDeleteUploadedShot(s: UploadedShot) {
    s.showMenu = false;
    this.tmpShot = s;
    this.currentSidebarType = SidebarTypeEnum.deleteAction;
  }

  public onDragStart(ev: any, s: UploadedShot) {
    this.tmpShot = s;
    const sub = defer(() => fromEvent(ev.target as HTMLElement, 'dragend'))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.onDragEnd();
        sub.unsubscribe();
      });
    this.inDragging = true;
    const ghostEl = ev.target.cloneNode(true) as HTMLElement;
    if (ghostEl && ghostEl.style) {
      ghostEl.style.opacity = '0';
      ghostEl.style.display = 'none';
      ev.dataTransfer.setDragImage(ghostEl, 0, 0);
    }
    setTimeout(() => { this.elementInDrag = document.getElementById(this.elementInDragId); });
  }

  public onDragOver(ev: any, shot?: OrderDetailsShot, style?: PhotographerOrderDetails) {
    ev.stopPropagation();
    if (shot && shot.shotApprovalStateId !== ShotStatusEnum.Approved) {
      ev.preventDefault();
      this.overedData = { shot, style };
    }
    this.dragElement(this.elementInDrag, ev);
  }

  public exitFromSidebar() {
    this.currentSidebarType = null;
    this.tmpShot = null;
    this.overedData = {};
  }

  public assignShot(orderImageId?: number, shotId?: number, loaderId?: string, invert?: number) {
    if (!this.tmpShot.orderImageId && this.tmpShot.hasOwnProperty('shotId')) {
      this.photoService.getOrderImageId(this.tmpShot.shotId!, loaderId ?? this.sidebarLoaderId)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: (id) => {
            this.photoService
              .subShot(id, shotId ?? this.overedData.shot.shotId, loaderId ?? this.sidebarLoaderId, invert ?? 0)
              .pipe(takeUntilDestroyed(this.#destroyRef))
              .subscribe(() => {
                this.exitFromSidebar();
                this.getPhotographerOrderDetails();
                this.getPhotographerUploadedShots();
              });
          },
          error: () => {
            this.exitFromSidebar();
          }
        });
    } else {
      this.photoService
        .assignShot(orderImageId ?? this.tmpShot.orderImageId, shotId ?? this.overedData.shot.shotId, loaderId ?? this.sidebarLoaderId)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.exitFromSidebar();
          this.getPhotographerOrderDetails();
          this.getPhotographerUploadedShots();
        });
    }
  }

  private getDataFromName(fileName: string): PhotoData {
    fileName = fileName.slice(0, fileName.lastIndexOf('.'));
    const sections = fileName.split('_');

    return {
      productName: sections[0].substring(0, 7),
      teamCode: sections[0].substring(7, 10),
      colorCode: sections[0].substring(10, 13),
      sizeCode: sections[1],
      shotType: sections[2],
      ratio: sections[3],
      extension: fileName.slice(fileName.lastIndexOf('.') + 1),
    }
  }

  public sortImagesByStyle() {
    const filesArray = this.uploadedShots;
    for (const file of filesArray) {

      if (file.fileName.length >= 20) {
        const data = this.getDataFromName(file.fileName);

        const correspondence = this.orderDetails.dataToView?.find(el => el.styleCode === data.productName && el.colorCode === data.colorCode && el.shots.filter(shot => shot.shotTypeCode === data.shotType).length > 0);
        if (!!correspondence) {
          const shot: UploadedShot = this.uploadedShots.find(shot => shot.fileName.includes(data.productName) && shot.fileName.includes(data.colorCode) && shot.fileName.includes(data.shotType))!;
          this.assignShot(shot.orderImageId, correspondence.shots.filter(shot => shot.shotTypeCode === data.shotType)[0].shotId);
        }
      }
    }
  }

  public sortImagesByColor(): void {
    const removedShots: Array<number> = [];
    this.sortedImages = {};
    this.uploadedShots.forEach(shot => {
      const data = this.getDataFromName(shot.fileName);
      const correspondence = this.orderDetails.dataToView?.find(el => el.styleCode === data.productName && el.colorCode === data.colorCode);
      if (!!correspondence) {
        if (this.sortedImages[`${data.productName}${data.colorCode}`]) {
          this.sortedImages[`${data.productName}${data.colorCode}`].push(shot);
        }
        else
          this.sortedImages[`${data.productName}${data.colorCode}`] = [shot];

        removedShots.push(shot.orderImageId);
      }

    });
    this.uploadedShots = this.uploadedShots.filter(s => !(removedShots.indexOf(s.orderImageId) > -1));
  }

  public onImagesUpload(event: any) {
    const files: FileList = event.target.files;
    this.photoService.uploadShot(files, this.orderId, this.orderDetailsLoaderId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(n => {
        if (n !== undefined) { this.getPhotographerUploadedShots(true); }
      });
  }

  public deleteShot() {
    this.photoService.deleteShot(this.tmpShot.orderImageId, this.sidebarLoaderId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.currentSidebarType = null;
        this.tmpShot = null;
        this.getPhotographerUploadedShots();
      });
  }

  public onBack() {
    this.exit.emit();
  }

  public onOrderComments() {
    this.currentSidebarType = SidebarTypeEnum.orderComments;
  }

  private dragElement(elementInDrag: HTMLElement, event: any) {
    if (elementInDrag) {
      elementInDrag.style.left = (event.clientX + 15) + 'px';
      elementInDrag.style.top = (event.clientY + 15) + 'px';
    }
  }

  private onDragEnd() {
    this.overedData.shot && this.overedData.style ? this.currentSidebarType = SidebarTypeEnum.customSidebar : this.overedData = {};
    this.inDragging = false;
    this.elementInDrag = null;
  }

  private searchTextSubscription() {
    this.subscription.add(this.topbarService.searchTextSubscribe
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        if (data !== this.orderDetails.search) {
          this.orderDetails.search = data;
          this.setStylesToView(true);
        }
      }));
  }

  private getPhotographerOrderDetails() {
    this.photoService.getPhotographerOrderDetails(this.orderId, this.orderDetailsLoaderId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        this.orderDetails.data = data.orderDetails;
        this.orderDetails.orderStatus = data.orderStatus;
        let index = -1;
        if (this.modelId) index = this.orderDetails.data.findIndex(x => x.modelId === this.modelId);
        this.setStylesToView();
      });
  }

  private getPhotographerUploadedShots(afterUpload?: boolean) {
    this.photoService.getPhotographerUploadedShots(this.orderId, this.uploadedShotsLoaderId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        this.uploadedShots = data;
        setTimeout(() => {
          this.sortImagesByColor();
          if (afterUpload) {
            this.sortImagesByStyle();
          }
        }, 500)
      });
  }

  public getShootingList() {
    this.#excelService.getShootingList(this.orderId, this.uploadedShotsLoaderId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (shootingList: Blob) => {
          const link = document.createElement('a');
          const url = window.URL.createObjectURL(shootingList);

          link.href = url;
          link.download = `ShootingList_${this.orderId}.xlsx`;
          link.click();

          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          console.error('Error retrieving shooting list:', err);
        }
      });
  }
}
